import { faArrowUpRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { BlockchainIcon } from "~/components/logos/blockchain";
import { ExternalLink } from "~/components/site/external-link";
import type { BlockchainId } from "~/lib/stablecoins.server";

type ChainId =
  | "ethereum"
  | "base"
  | "arbitrum"
  | "optimism"
  | "avalanche"
  | "polygon"
  | "ethereumclassic"
  | "celo"
  | "solana"
  | "stellar";

type Transfers = Readonly<{
  chainId: BlockchainId;
  name: string;
  estCost: string;
  actualCost: string;
  permitTxn?: string;
  transferTxn: string;
}>;

type Props = Readonly<{
  selectedTransfers?: ChainId[];
}>;

const transfers: Transfers[] = [
  {
    chainId: "ethereum",
    name: "Ethereum",
    estCost: "$22.64",
    actualCost: "$17.47",
    permitTxn:
      "https://etherscan.io/tx/0x0d96cc71d981ccb5265ec25e247f333372813fb09c63e2702f9a50960d08651b",
    transferTxn:
      "https://etherscan.io/tx/0x5b0b57256ab2d6e5ef0f7b9f8445854d05caf0ff9bd44e29579ba2e6f8b66192",
  },
  {
    chainId: "base",
    name: "Base",
    estCost: "$1.52",
    actualCost: "$1.11",
    permitTxn:
      "https://basescan.org/tx/0x49c9c6c3e6b5ca171ffb8f7a13b212115f1e59d1dc18eb95ea0b1adece9b8cf3",
    transferTxn:
      "https://basescan.org/tx/0xef4ef7b443867186065777bb821245c3007cd0f1fdcacecd1a74f80e045e7e7b",
  },
  {
    chainId: "arbitrum",
    name: "Arbitrum",
    estCost: "$1.05",
    actualCost: "$0.35",
    permitTxn:
      "https://arbiscan.io/tx/0xe8d9c42b6c2c9734b9988900a3774014cca722ba029b6bece383efedd628a384",
    transferTxn:
      "https://arbiscan.io/tx/0xedbf1f180f7edd95d6962bb5447b5d0f3554905f6e0af961f1273b9a73eca513",
  },
  {
    chainId: "optimism",
    name: "Optimism",
    estCost: "$1.02",
    actualCost: "$1.16",
    permitTxn:
      "https://optimistic.etherscan.io/tx/0x6d218442667a32b13389aca0257c5d6e95481d970b825deee9f9e371bb9ded1d",
    transferTxn:
      "https://optimistic.etherscan.io/tx/0xbb52e3cf43d3a599604d1ce5e0349bafe6aecff5962d39e6320d3e0c91913993",
  },
  {
    chainId: "avalanche",
    name: "Avalanche",
    estCost: "$0.26",
    actualCost: "$0.26",
    permitTxn:
      "https://snowtrace.io/tx/0x7687cee536aeefd2ea9acfc94f3206824a7158578185038e154de9d9a7d8502f?chainId=43114",
    transferTxn:
      "https://avascan.info/blockchain/c/tx/0x15eeabb97eca9c5792503c8d7b5a0a418faea63707cc74c1b6bacb78e2d3725f",
  },
  {
    chainId: "polygon",
    name: "Polygon",
    estCost: "$0.03",
    actualCost: "$0.04",
    permitTxn:
      "https://polygonscan.com/tx/0x3b15cb341897f26f56c460e63551f5f64ae1ce8c56db59e48c9069bb4d03c764",
    transferTxn:
      "https://polygonscan.com/tx/0x8f13f02b9e6ac683dae6af9e5a068a5824f7b568154b1d6cd22ec7780a332e82",
  },
  {
    chainId: "ethereum-classic",
    name: "Ethereum Classic",
    estCost: "$0.01",
    actualCost: "$0.01",
    permitTxn:
      "https://etc.blockscout.com/tx/0x111708eb78abb709e77fa7cf647c63b24c163ae4759a480eb608ed41647916e4",
    transferTxn:
      "https://etc.blockscout.com/tx/0x6e344397abfa110285a48c30e7fb178f7ccde71d4f0a79abd68059a7d39b1d70",
  },
  {
    chainId: "celo",
    name: "Celo",
    estCost: "$0.01",
    actualCost: "$0.001",
    permitTxn:
      "https://celoscan.io/tx/0x4a01a05bd4516a8629b0420c6097b92d903bfec01892ce64b8eb03215021d773",
    transferTxn:
      "https://celoscan.io/tx/0xc2a3f11030d5975b49707de8ea163b0006e718ac1416681ee422c4ea72f6eba9",
  },
  {
    chainId: "solana",
    name: "Solana",
    estCost: "<$0.01",
    actualCost: "$0.0005",
    transferTxn:
      "https://explorer.solana.com/tx/2gfRmsMBKHXXNom49uFbcChdmfpPDZEvyXoBoKvtA7YY8hXjiHko4utK3Tc4hq8mNV8MbRK5hsyKLQwsJAQALpWs",
  },
  {
    chainId: "stellar",
    name: "Stellar",
    estCost: "<$0.01",
    actualCost: "$0.000001",
    transferTxn:
      "https://stellar.expert/explorer/public/tx/c851361ff1c349fd4a24bfaa4fc129bcfc295181317a06dbb7040d190db20d62",
  },
];

export function TransferCosts({ selectedTransfers }: Props): JSX.Element {
  const transferMap = new Map<string, Transfers>();

  transfers.forEach((transfer) => {
    transferMap.set(transfer.chainId, transfer);
  });

  let filteredTransfers;

  if (selectedTransfers === undefined || selectedTransfers === null) {
    filteredTransfers = transfers;
  } else {
    filteredTransfers = selectedTransfers
      .map((chainId) => transferMap.get(chainId) as Transfers)
      .filter(Boolean);
  }

  return (
    <div className="border border-primary round overflow-x-scroll bg-white dark:bg-black p-6 md:p-8 mt-8 mb-8">
      <table className="table-auto m-0 whitespace-nowrap">
        <thead>
          <tr>
            <th>Chain</th>
            <th className="px-4">Estimated Cost</th>
            <th className="px-4">Actual Cost</th>
            <th className="text-right">Txn(s)</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransfers.map((t) => (
            <tr key={t.transferTxn}>
              <td>
                <BlockchainIcon
                  chainId={t.chainId}
                  className="mr-2 inline-block"
                />
                {t.name}
              </td>
              <td className="px-4">{t.estCost}</td>
              <td className="px-4">{t.actualCost}</td>
              <td className="text-right align-middle">
                {t.permitTxn && (
                  <ExternalLink title="View on Explorer" href={t.permitTxn}>
                    <FontAwesomeIcon
                      className="h-4 w-4 text-secondary mr-3"
                      icon={faArrowUpRight}
                    />
                  </ExternalLink>
                )}
                <ExternalLink title="View on Explorer" href={t.transferTxn}>
                  <FontAwesomeIcon
                    className="h-4 w-4 text-secondary"
                    icon={faArrowUpRight}
                  />
                </ExternalLink>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
